import {
  DiningFlowEntryPoint,
  DiningReservationMode,
  DiningStepId,
  DiningFlowEventType,
} from '@msc-b2c/interfaces/enums';
import type { DiningFlowConfig } from '@msc-b2c/interfaces/interfaces';

/**
 * DiningMultistepFlow
 *
 * Canonical state machine for the Dining drawer multi-step flow.
 *
 * ---------------------------------------------------------------------------
 * 1) What this module is responsible for
 * ---------------------------------------------------------------------------
 * - It defines the navigation rules for the dining flow.
 * - It does not render UI.
 * - It does not dispatch Redux actions.
 * - It does not execute side effects.
 *
 * In short: this file is the "flow brain" only.
 *
 * ---------------------------------------------------------------------------
 * 2) Why this is a state machine
 * ---------------------------------------------------------------------------
 * - The active step is derived from explicit events (NEXT, BACK, SELECT_RESERVATION_MODE, ...).
 * - Each transition is deterministic and pure: (state, event) => newState.
 * - Invalid transitions are safely ignored (no-op) and logged in non-production builds.
 *
 * This keeps behavior testable and predictable, especially when the flow grows.
 *
 * ---------------------------------------------------------------------------
 * 3) Core model used by this file
 * ---------------------------------------------------------------------------
 * - `steps`: full ordered list for the current entry point.
 * - `skippedSteps`: dynamic subset currently inactive.
 * - `currentStep`: current active node in the flow.
 * - `reservationMode`: branch selector set at CHOOSE_WHEN_BOOK.
 *
 * Important: optional steps are never inserted/removed from `steps` at runtime.
 * They are toggled through `skippedSteps`.
 *
 * ---------------------------------------------------------------------------
 * 4) Current flow shape (high level)
 * ---------------------------------------------------------------------------
 * PRODUCT_CARD:
 *   PAX_SELECTION -> CHOOSE_WHEN_BOOK -> (branch by reservation mode) -> SUCCESS
 * CART / ALREADY_PURCHASED:
 *   CHOOSE_WHEN_BOOK -> (branch by reservation mode) -> SUCCESS
 *
 * WITH_TIMESLOT enables DATE_SELECTION + TIME_SELECTION.
 * WITHOUT_TIMESLOT keeps those steps skipped.
 *
 * Note: `CONFIRM` exists as a step identifier for compatibility/integration use,
 * but the current ordered lists route to SUCCESS directly.
 *
 * ---------------------------------------------------------------------------
 * 5) Rules every maintainer should keep
 * ---------------------------------------------------------------------------
 * - Entry point defines the baseline step order.
 * - Branching is controlled only by `SELECT_RESERVATION_MODE`.
 * - Guards (NEXT_GUARDS_BY_STEP) are the single source for "can move next" checks.
 * - Edit events (EDIT_DATE, EDIT_TIME, EDIT_PAX) are explicit jumps.
 *
 * ---------------------------------------------------------------------------
 * 6) How to safely extend this file
 * ---------------------------------------------------------------------------
 * A) Add a new step in the existing branch (example: DINING_ROOM_SELECTION)
 * 1. Add enum value in `DiningStepId`.
 * 2. Add the step in each relevant `ALL_STEPS_BY_ENTRY_POINT` list.
 * 3. If optional, include it in `INITIAL_SKIPPED_STEPS_BY_ENTRY_POINT`.
 * 4. If branch-controlled, update `ACTIVATED_STEPS_BY_MODE_AND_ENTRY_POINT`.
 * 5. If users can jump to it from recap/edit actions, map an edit event in `EDIT_EVENT_TO_STEP`.
 *
 * B) Add a new branch mode (example: WAITLIST)
 * 1. Add enum value in `DiningReservationMode`.
 * 2. Add branch activation mapping under `ACTIVATED_STEPS_BY_MODE_AND_ENTRY_POINT`.
 * 3. Ensure UI dispatches SELECT_RESERVATION_MODE with the new mode.
 * 4. Add/update NEXT guard if progression must be constrained.
 *
 * C) Add a new event (example: SKIP_TO_RECAP)
 * 1. Extend `DiningFlowEventType` and `DiningFlowEvent` union.
 * 2. Handle it in the reducer switch.
 * 3. Keep exhaustive switch behavior (`never` checks).
 *
 * ---------------------------------------------------------------------------
 * 7) Practical checklist before merging flow changes
 * ---------------------------------------------------------------------------
 * - Verify each entry point initial step is still valid (first non-skipped step).
 * - Verify NEXT/BACK skip logic still traverses correctly.
 * - Verify new branches cannot dead-end (no undefined next step where one is expected).
 * - Verify edit jumps cannot target currently skipped steps unless intended.
 * - Update this header doc if behavior contracts changed.
 */

// ---------------------------------------------------------------------------
// Enums and flow config — re-exported from @msc-b2c/interfaces for consumers
// ---------------------------------------------------------------------------

export {
  DiningFlowEntryPoint,
  DiningReservationMode,
  DiningStepId,
  DiningFlowEventType,
} from '@msc-b2c/interfaces/enums';

export type { DiningFlowConfig } from '@msc-b2c/interfaces/interfaces';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DiningFlowEvent =
  | { type: DiningFlowEventType.NEXT }
  | { type: DiningFlowEventType.BACK }
  | { type: DiningFlowEventType.SELECT_RESERVATION_MODE; mode: DiningReservationMode }
  | { type: DiningFlowEventType.EDIT_DATE_TIME }
  | { type: DiningFlowEventType.EDIT_DATE }
  | { type: DiningFlowEventType.EDIT_TIME }
  | { type: DiningFlowEventType.EDIT_PAX }
  | { type: DiningFlowEventType.EDIT_CHOOSE_WHEN_BOOK }
  | { type: DiningFlowEventType.RESET };

export type DiningFlowState = {
  /** Full ordered step list for this entry point; never changes after initialization. */
  readonly steps: readonly DiningStepId[];
  /** Currently inactive steps; navigation (NEXT/BACK) skips these transparently. */
  readonly skippedSteps: ReadonlySet<DiningStepId>;
  readonly currentStep: DiningStepId;
  readonly initialStep: DiningStepId;
  readonly entryPoint: DiningFlowEntryPoint;
  /** Undefined until the user picks a mode at CHOOSE_WHEN_BOOK. */
  readonly reservationMode?: DiningReservationMode;
};

// ---------------------------------------------------------------------------
// Internal helpers (not exported – consumed only within this module)
// ---------------------------------------------------------------------------

/**
 * Maps explicit "edit" events to their target step.
 *
 * Why centralize this map:
 * - avoids scattered conditionals in the reducer
 * - makes jump behavior easy to audit
 * - makes future additions low-risk
 */
const EDIT_EVENT_TO_STEP: Readonly<Partial<Record<DiningFlowEventType, DiningStepId>>> = {
  [DiningFlowEventType.EDIT_DATE]: DiningStepId.DATE_SELECTION,
  [DiningFlowEventType.EDIT_TIME]: DiningStepId.TIME_SELECTION,
  [DiningFlowEventType.EDIT_DATE_TIME]: DiningStepId.DATE_TIME_SELECTION,
  [DiningFlowEventType.EDIT_CHOOSE_WHEN_BOOK]: DiningStepId.CHOOSE_WHEN_BOOK,
};

const resolveEditPaxTarget = (entryPoint: DiningFlowEntryPoint): DiningStepId =>
  entryPoint === DiningFlowEntryPoint.PRODUCT_CARD
    ? DiningStepId.PAX_SELECTION
    : DiningStepId.PAX_SELECTION_EDIT;

/**
 * Complete ordered step list per entry point.
 *
 * Keep this as the full superset for each entry point.
 * Optional steps are still listed here and become active/inactive through `skippedSteps`.
 * This avoids runtime array mutations and keeps traversal deterministic.
 */
const ALL_STEPS_BY_ENTRY_POINT: Readonly<Record<DiningFlowEntryPoint, readonly DiningStepId[]>> = {
  [DiningFlowEntryPoint.PRODUCT_CARD]: [
    DiningStepId.PAX_SELECTION,
    DiningStepId.CHOOSE_WHEN_BOOK,
    DiningStepId.DATE_TIME_SELECTION,
    DiningStepId.RESERVATION_RECAP,
    DiningStepId.SUCCESS,
  ],
  [DiningFlowEntryPoint.CART]: [
    DiningStepId.CART_RESERVATION_RECAP,
    DiningStepId.PAX_SELECTION_EDIT,
    DiningStepId.DATE_TIME_SELECTION,
    DiningStepId.RESERVATION_RECAP,
    DiningStepId.SUCCESS,
  ],
  [DiningFlowEntryPoint.ALREADY_PURCHASED]: [
    DiningStepId.CART_RESERVATION_RECAP,
    DiningStepId.DATE_TIME_SELECTION,
    DiningStepId.RESERVATION_RECAP,
    DiningStepId.SUCCESS,
  ],
};

/**
 * Default skipped steps per entry point.
 *
 * These are the optional steps that start inactive before the user chooses a mode.
 */
const INITIAL_SKIPPED_STEPS_BY_ENTRY_POINT: Readonly<
  Record<DiningFlowEntryPoint, ReadonlySet<DiningStepId>>
> = {
  [DiningFlowEntryPoint.PRODUCT_CARD]: new Set([
    DiningStepId.DATE_TIME_SELECTION,
    DiningStepId.RESERVATION_RECAP,
  ]),
  [DiningFlowEntryPoint.CART]: new Set([
    DiningStepId.PAX_SELECTION_EDIT,
    DiningStepId.DATE_TIME_SELECTION,
    DiningStepId.RESERVATION_RECAP,
  ]),
  [DiningFlowEntryPoint.ALREADY_PURCHASED]: new Set([
    DiningStepId.DATE_TIME_SELECTION,
    DiningStepId.RESERVATION_RECAP,
  ]),
};

/**
 * Branch activation map by reservation mode and entry point.
 *
 * Example:
 * - WITH_TIMESLOT activates DATE/TIME steps.
 * - WITHOUT_TIMESLOT activates none of those optional steps.
 *
 * Add new mode behavior here instead of spreading conditions across reducer cases.
 */
const ACTIVATED_STEPS_BY_MODE_AND_ENTRY_POINT: Readonly<
  Record<DiningReservationMode, Readonly<Record<DiningFlowEntryPoint, ReadonlySet<DiningStepId>>>>
> = {
  [DiningReservationMode.WITH_TIMESLOT]: {
    [DiningFlowEntryPoint.PRODUCT_CARD]: new Set([
      DiningStepId.DATE_TIME_SELECTION,
      DiningStepId.RESERVATION_RECAP,
    ]),
    [DiningFlowEntryPoint.CART]: new Set([
      DiningStepId.DATE_TIME_SELECTION,
      DiningStepId.RESERVATION_RECAP,
    ]),
    [DiningFlowEntryPoint.ALREADY_PURCHASED]: new Set([
      DiningStepId.DATE_TIME_SELECTION,
      DiningStepId.RESERVATION_RECAP,
    ]),
  },
  [DiningReservationMode.WITHOUT_TIMESLOT]: {
    [DiningFlowEntryPoint.PRODUCT_CARD]: new Set(),
    [DiningFlowEntryPoint.CART]: new Set(),
    [DiningFlowEntryPoint.ALREADY_PURCHASED]: new Set(),
  },
};

const getInitialSkippedSteps = (entryPoint: DiningFlowEntryPoint): ReadonlySet<DiningStepId> =>
  new Set(INITIAL_SKIPPED_STEPS_BY_ENTRY_POINT[entryPoint]);

const getSkippedStepsAfterModeSelection = (
  entryPoint: DiningFlowEntryPoint,
  mode: DiningReservationMode
): ReadonlySet<DiningStepId> => {
  const initialSkipped = INITIAL_SKIPPED_STEPS_BY_ENTRY_POINT[entryPoint];
  const activated = ACTIVATED_STEPS_BY_MODE_AND_ENTRY_POINT[mode][entryPoint];
  return new Set([...initialSkipped].filter((step) => !activated.has(step)));
};

/**
 * Optional guard functions for NEXT per step.
 *
 * If a guard exists and returns false, NEXT is blocked.
 * This is the right place for progression prerequisites.
 */
const NEXT_GUARDS_BY_STEP: Readonly<
  Partial<Record<DiningStepId, (state: DiningFlowState) => boolean>>
> = {
  [DiningStepId.CHOOSE_WHEN_BOOK]: (state) => state.reservationMode !== undefined,
};

/**
 * Returns the next active (non-skipped) step in a direction.
 *
 * `direction = 1`  => forward (NEXT)
 * `direction = -1` => backward (BACK)
 *
 * Returns undefined if no active step exists in that direction.
 */
const getNextActiveStep = (
  steps: readonly DiningStepId[],
  skippedSteps: ReadonlySet<DiningStepId>,
  from: DiningStepId,
  direction: 1 | -1
): DiningStepId | undefined => {
  let idx = steps.indexOf(from) + direction;
  while (idx >= 0 && idx < steps.length) {
    if (!skippedSteps.has(steps[idx])) return steps[idx];
    idx += direction;
  }
  return undefined;
};

const emitInvalidTransitionDiag = (
  currentStep: DiningStepId,
  event: DiningFlowEvent,
  steps: readonly DiningStepId[]
): void => {
  // eslint-disable-next-line no-console
  console.warn(
    `[DiningMultistepFlow] Ignored invalid transition. currentStep=${currentStep} event=${
      event.type
    } steps=[${steps.join(',')}]`
  );
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns active steps from current state (ordered, excluding skipped ones).
 * Useful for debugging, inspection, and UI progress indicators.
 */
export const getActiveSteps = (state: DiningFlowState): readonly DiningStepId[] =>
  state.steps.filter((s) => !state.skippedSteps.has(s));

/**
 * Builds the initial flow state from entry-point config.
 *
 * `initialStep` is computed as the first step that is not skipped by default.
 * If you change defaults, verify this still points to the intended start.
 */
export const initializeDiningFlowState = (config: DiningFlowConfig): DiningFlowState => {
  const steps = ALL_STEPS_BY_ENTRY_POINT[config.entryPoint];
  const skippedSteps = getInitialSkippedSteps(config.entryPoint);
  // The initial step is the first non-skipped step in the list.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const defaultInitialStep = steps.find((s) => !skippedSteps.has(s))!;
  const initialStep =
    config.initialStep && steps.includes(config.initialStep)
      ? config.initialStep
      : defaultInitialStep;
  return {
    steps,
    skippedSteps,
    initialStep,
    currentStep: initialStep,
    entryPoint: config.entryPoint,
  };
};

/**
 * Pure reducer for flow transitions.
 *
 * Contract:
 * - valid event => returns a new state object
 * - invalid event => returns same reference (no-op)
 *
 * Returning the same reference for invalid transitions allows consumers to detect no-op changes
 * via referential equality when needed.
 */
export const transitionDiningFlow = (
  state: DiningFlowState,
  event: DiningFlowEvent
): DiningFlowState => {
  const invalid = (): DiningFlowState => {
    emitInvalidTransitionDiag(state.currentStep, event, state.steps);
    return state;
  };

  if (!state.steps.includes(state.currentStep)) {
    return invalid();
  }

  switch (event.type) {
    case DiningFlowEventType.NEXT: {
      const guard = NEXT_GUARDS_BY_STEP[state.currentStep];
      if (guard !== undefined && !guard(state)) {
        return invalid();
      }

      const nextStep = getNextActiveStep(state.steps, state.skippedSteps, state.currentStep, 1);

      if (nextStep === undefined) {
        return invalid();
      }

      return { ...state, currentStep: nextStep };
    }

    case DiningFlowEventType.BACK: {
      const previousStep = getNextActiveStep(
        state.steps,
        state.skippedSteps,
        state.currentStep,
        -1
      );

      if (previousStep === undefined) {
        return invalid();
      }

      return { ...state, currentStep: previousStep };
    }

    case DiningFlowEventType.SELECT_RESERVATION_MODE: {
      // Mode selection is valid at CHOOSE_WHEN_BOOK, CART recap, PAX_SELECTION_EDIT,
      // and DATE_TIME_SELECTION.
      // CART recap/PAX edit/DATE_TIME_SELECTION need this to activate optional steps
      // in edit and already-purchased flows before advancing.
      const canSelectModeFromCurrentStep =
        state.currentStep === DiningStepId.CHOOSE_WHEN_BOOK ||
        state.currentStep === DiningStepId.CART_RESERVATION_RECAP ||
        state.currentStep === DiningStepId.PAX_SELECTION_EDIT ||
        state.currentStep === DiningStepId.DATE_TIME_SELECTION;

      if (!canSelectModeFromCurrentStep) {
        return invalid();
      }

      // Apply per-entry-point branch activation based on the selected mode.
      // Current step stays at CHOOSE_WHEN_BOOK; user must press NEXT to advance.
      const newSkipped = getSkippedStepsAfterModeSelection(state.entryPoint, event.mode);
      return { ...state, skippedSteps: newSkipped, reservationMode: event.mode };
    }

    case DiningFlowEventType.EDIT_DATE_TIME:
    case DiningFlowEventType.EDIT_DATE:
    case DiningFlowEventType.EDIT_TIME:
    case DiningFlowEventType.EDIT_CHOOSE_WHEN_BOOK: {
      const target = EDIT_EVENT_TO_STEP[event.type];

      if (target === undefined || !state.steps.includes(target) || state.skippedSteps.has(target)) {
        return invalid();
      }

      return { ...state, currentStep: target };
    }

    case DiningFlowEventType.EDIT_PAX: {
      const target = resolveEditPaxTarget(state.entryPoint);
      const isTargetSkippableEditStep = target === DiningStepId.PAX_SELECTION_EDIT;

      if (
        !state.steps.includes(target) ||
        (state.skippedSteps.has(target) && !isTargetSkippableEditStep)
      ) {
        return invalid();
      }

      return { ...state, currentStep: target };
    }

    case DiningFlowEventType.RESET:
      return { ...state, currentStep: state.initialStep };

    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
};
